import { EditOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import {
  productStatusColors,
  productStatusLabels,
} from 'apps/investcore/constants/fundProduct';
import InfoCard from 'common/ui/info-card';
import { BuildingIcon, ChartBarIcon, InfoIcon, PhoneIcon, TagIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';
import { cloudflareCdn } from 'utils/image';

const ItemDetail = ({ item }) => {
  const {
    id,
    created_at,
    updated_at,
    symbol,
    name,
    exchange,
    logo,
    status,
    icb_industry_code,
    icb_industry,
    icb_supersector_code,
    icb_supersector,
    icb_sector_code,
    icb_sector,
    icb_subsector_code,
    icb_subsector,
    isin,
    industry_name,
    super_sector,
    sector,
    sub_sector,
    sub_sector_code,
    charter_capital,
    number_of_employee,
    issue_share,
    listed_value,
    free_float_rate,
    founding_date,
    listing_date,
    address,
    website,
    telephone,
    email,
    fax,
    company_profile,
  } = item || {};

  return (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex gap-4">
          {logo ? (
            <img
              src={cloudflareCdn(logo, 100)}
              alt="Logo"
              className="w-14 rounded object-contain"
            />
          ) : null}
          <InfoCard className="flex-grow" label="Symbol" value={symbol} />
        </div>
        <InfoCard className="flex-grow" label="Name" value={name} />
        <InfoCard
          className="flex-grow"
          label="Status"
          value={
            <Tag color={productStatusColors[status]}>
              {productStatusLabels[status] || status}
            </Tag>
          }
        />
        <InfoCard className="flex-grow" label="Exchange" value={exchange} />
      </div>

      {/* ICB Classification */}
      <div className="flex items-center justify-between pt-2 border-b pb-1">
        <h3 className="text-base font-medium text-gray-800 flex items-center gap-1">
          <TagIcon size={18} />
          ICB Classification
        </h3>
        <Link
          to={`/admin/products/stocks/${id}/edit`}
          className="flex items-center gap-1 text-sm"
        >
          <EditOutlined /> Edit ICB
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard
          label="Industry (L1)"
          value={
            icb_industry_code ? (
              <Link to={`/admin/stock-icb/industries/${icb_industry_code}`} className="flex flex-col gap-0.5">
                <Tag color="blue" className="w-fit">{icb_industry_code}</Tag>
                <span className="text-sm">{icb_industry?.name ?? ''}</span>
              </Link>
            ) : '-'
          }
        />
        <InfoCard
          label="SuperSector (L2)"
          value={
            icb_supersector_code ? (
              <Link to={`/admin/stock-icb/supersectors/${icb_supersector_code}`} className="flex flex-col gap-0.5">
                <Tag color="cyan" className="w-fit">{icb_supersector_code}</Tag>
                <span className="text-sm">{icb_supersector?.name ?? ''}</span>
              </Link>
            ) : '-'
          }
        />
        <InfoCard
          label="Sector (L3)"
          value={
            icb_sector_code ? (
              <Link to={`/admin/stock-icb/sectors/${icb_sector_code}`} className="flex flex-col gap-0.5">
                <Tag color="green" className="w-fit">{icb_sector_code}</Tag>
                <span className="text-sm">{icb_sector?.name ?? ''}</span>
              </Link>
            ) : '-'
          }
        />
        <InfoCard
          label="SubSector (L4)"
          value={
            icb_subsector_code ? (
              <Link to={`/admin/stock-icb/subsectors/${icb_subsector_code}`} className="flex flex-col gap-0.5">
                <Tag color="orange" className="w-fit">{icb_subsector_code}</Tag>
                <span className="text-sm">{icb_subsector?.name ?? ''}</span>
              </Link>
            ) : '-'
          }
        />
      </div>

      {/* Stock Profile */}
      <div className="flex items-center justify-between pt-2 border-b pb-1">
        <h3 className="text-base font-medium text-gray-800 flex items-center gap-1">
          <BuildingIcon size={18} />
          Stock Profile
        </h3>
        <Link
          to={`/admin/products/stocks/${id}/edit`}
          className="flex items-center gap-1 text-sm"
        >
          <EditOutlined /> Edit
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="ISIN" value={isin || '-'} />
      </div>

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center gap-1">
        <ChartBarIcon size={18} />
        Industry / Sector
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="Industry" value={industry_name || '-'} />
        <InfoCard label="Super Sector" value={super_sector || '-'} />
        <InfoCard label="Sector" value={sector || '-'} />
        <InfoCard label="Sub Sector" value={sub_sector || '-'} />
        <InfoCard label="Sub Sector Code" value={sub_sector_code || '-'} />
      </div>

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center gap-1">
        Fundamentals
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard
          label="Charter Capital"
          value={charter_capital?.toLocaleString() || '-'}
        />
        <InfoCard
          label="Number of Employees"
          value={number_of_employee?.toLocaleString() || '-'}
        />
        <InfoCard
          label="Issue Share"
          value={issue_share?.toLocaleString() || '-'}
        />
        <InfoCard
          label="Listed Value"
          value={listed_value?.toLocaleString() || '-'}
        />
        <InfoCard
          label="Free Float Rate"
          value={free_float_rate != null ? `${free_float_rate}%` : '-'}
        />
        <InfoCard label="Founding Date" value={founding_date || '-'} />
        <InfoCard label="Listing Date" value={listing_date || '-'} />
      </div>

      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center gap-1">
        <PhoneIcon size={18} />
        Contact
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="Address" value={address || '-'} />
        <InfoCard
          label="Website"
          value={
            website ? (
              <a href={website} target="_blank" rel="noreferrer">
                {website}
              </a>
            ) : (
              '-'
            )
          }
        />
        <InfoCard label="Telephone" value={telephone || '-'} />
        <InfoCard label="Email" value={email || '-'} />
        <InfoCard label="Fax" value={fax || '-'} />
      </div>

      {company_profile && (
        <>
          <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center gap-1">
            <InfoIcon size={18} />
            Company Profile
          </h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {company_profile}
          </p>
        </>
      )}

      {/* Metadata */}
      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Created At" value={formatDateTime(created_at)} />
          <InfoCard label="Updated At" value={formatDateTime(updated_at)} />
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
